import type { StoreCustomField } from '../services/api';

/**
 * Renders a project's user-defined store attributes as real input controls inside
 * the Add/Edit Store form.
 *
 * The definitions come from Master Data → Stores → Custom Fields, so this is the
 * one place that turns a stored `field_type` into a control. It deliberately keeps
 * no state of its own: values live on the parent form so they submit with the rest
 * of the store, and the server re-validates every value against the same option
 * lists — the control merely makes the valid choices the easy ones.
 */

interface Props {
  fields: StoreCustomField[];
  values: Record<string, unknown>;
  onChange: (fieldId: string, value: unknown) => void;
}

const labelFor = (field: StoreCustomField) => (
  <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={`cf-${field.id}`}>
    {field.label}
    {field.isRequired && <span className="ml-0.5 text-red-500">*</span>}
  </label>
);

const INPUT_CLASS =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

export default function StoreCustomFieldInputs({ fields, values, onChange }: Props) {
  if (fields.length === 0) return null;

  return (
    <div className="space-y-4 border-t border-gray-100 pt-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
        Additional Details
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {fields.map((field) => {
          const value = values[field.id];
          // Multi-select spans the full row: its chips wrap, and a half-width box
          // makes three or more selections unreadable.
          const fullWidth = field.fieldType === 'multiple_choice';

          return (
            <div key={field.id} className={fullWidth ? 'sm:col-span-2' : undefined}>
              {labelFor(field)}

              {field.fieldType === 'text' && (
                <input
                  id={`cf-${field.id}`}
                  type="text"
                  value={typeof value === 'string' ? value : ''}
                  onChange={(e) => onChange(field.id, e.target.value)}
                  required={field.isRequired}
                  className={INPUT_CLASS}
                />
              )}

              {field.fieldType === 'number' && (
                <input
                  id={`cf-${field.id}`}
                  type="number"
                  value={value == null ? '' : String(value)}
                  onChange={(e) => onChange(field.id, e.target.value)}
                  required={field.isRequired}
                  className={INPUT_CLASS}
                />
              )}

              {field.fieldType === 'date' && (
                <input
                  id={`cf-${field.id}`}
                  type="date"
                  value={typeof value === 'string' ? value.slice(0, 10) : ''}
                  onChange={(e) => onChange(field.id, e.target.value)}
                  required={field.isRequired}
                  className={INPUT_CLASS}
                />
              )}

              {field.fieldType === 'boolean' && (
                // A select rather than a checkbox: an unticked checkbox cannot
                // distinguish "No" from "not answered yet", which matters for a
                // field that may be required.
                <select
                  id={`cf-${field.id}`}
                  value={value === true ? 'yes' : value === false ? 'no' : ''}
                  onChange={(e) =>
                    onChange(field.id, e.target.value === '' ? '' : e.target.value === 'yes')
                  }
                  required={field.isRequired}
                  className={INPUT_CLASS}
                >
                  <option value="">Select…</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              )}

              {field.fieldType === 'dropdown' && (
                <select
                  id={`cf-${field.id}`}
                  value={typeof value === 'string' ? value : ''}
                  onChange={(e) => onChange(field.id, e.target.value)}
                  required={field.isRequired}
                  className={INPUT_CLASS}
                >
                  <option value="">Select…</option>
                  {(field.options ?? []).map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              )}

              {field.fieldType === 'multiple_choice' && (
                <MultiSelect
                  field={field}
                  selected={Array.isArray(value) ? (value as string[]) : []}
                  onChange={(next) => onChange(field.id, next)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Checkbox group rather than a native `<select multiple>`: the native control needs
 * ctrl/cmd-click to pick more than one, which users routinely miss and which has no
 * touch equivalent.
 */
function MultiSelect({
  field, selected, onChange,
}: {
  field: StoreCustomField;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const toggle = (option: string) => {
    onChange(
      selected.includes(option)
        ? selected.filter((s) => s !== option)
        : [...selected, option],
    );
  };

  return (
    <div className="flex flex-wrap gap-2 rounded-lg border border-gray-300 p-2">
      {(field.options ?? []).map((option) => {
        const isOn = selected.includes(option);
        return (
          <button
            key={option}
            type="button"
            onClick={() => toggle(option)}
            aria-pressed={isOn}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              isOn
                ? 'border-primary-600 bg-primary-50 text-primary-700'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {option}
          </button>
        );
      })}
      {(field.options ?? []).length === 0 && (
        <span className="px-1 text-xs text-gray-400">No values defined for this field yet.</span>
      )}
    </div>
  );
}
